import React, { useContext, useRef, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import {
  FarmRiskType,
  FarmScheduleCoverageType,
  FarmScheduleCoverageTypesByState,
  lookupsJson,
  policyType,
  StructureCategory
} from '@ourbranch/lookups';
import { observer } from 'mobx-react';
import { useFormikContext, getIn } from 'formik';

import { appConfig } from 'brand-config';
import { useFarmPolicyContext } from 'common/hooks/use-farm-policy-context';
import externalLinkIcon from 'core/assets/svg/external-link.svg';
import { useToast } from 'core/components/toast';
import withDatePicker from 'core/components/with-date-picker';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import {
  InlandMarineForm,
  UnscheduledPersonalPropertyForm,
  RelatedPrivateStructuresForm,
  FarmIncomeCoverageForm
} from '../sub-risks';
import { Banner } from 'common/components/banner';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import { useFarmOpenSections } from 'common/hooks/farm-open-sections-context';
import { NotificationCard } from 'core/components/notification-card';
import { getDwellingHeatingOptionsForFO6, getFO6ConstructionTypeOptions } from 'common/helpers/farm-helpers';
import { Tooltip } from 'core/components/tooltip';
import editIcon from 'core/assets/svg/edit.svg';
import { AuthContext } from 'core/components/auth';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import { BrandFormField } from 'common/components/farm/brand-form-field';

// Import new components and utilities
import { useFO6BusinessLogic } from './useFO6BusinessLogic';
import {
  validateRoofYear,
  getLossSettlementOptions,
  getRoofLossSettlementOptions,
  validateRoofLossSettlement,
  getConfinementBuildingAge,
  isUnderwriter
} from './FO6.utils';
import BarnOutbuildingFields from './BarnOutbuildingFields';
import SilosGrainBinsFields from './SilosGrainBinsFields';
import ConfinementBuildingFields from './ConfinementBuildingFields';
import HoopBuildingFields from './HoopBuildingFields';

// Import exclusion components
import { ExteriorWallSurfacingExclusionSection } from '../exclusions';

import useStyles from '../risk.styles';

// Form options constants
const bqOptions = lookupsJson.farm.buildingQuality;
const barnRtOptions = lookupsJson.farm.barnRoofType;
const structureCategoriesByState = lookupsJson.farm.structureCategoriesByState;
const windHailDeductibleOptions = lookupsJson.farm.windstormOrHail;

// Note: buildingTypes will be filtered by state later in the component

const riskType = FarmRiskType.Fo6Structures;

const FO6 = ({ item, index, fieldName, removeRisk, setRiskFieldnameToEdit, canRemoveRisk, locationAddress }) => {
  const classes = useStyles();
  const { disableForm: disableRiskForm } = useActiveFormState(fieldName);
  const { isCurrentSectionOpen, toggleSection } = useFarmOpenSections(fieldName);
  const { setFieldValue, initialValues, errors, values } = useFormikContext();
  const { inPolicyRoute } = useRouteHelper();

  const toast = useToast();
  const session = useContext(AuthContext);
  const address = locationAddress;
  // Farm product (F / Z / Y) — drives product-specific coverage gating below. #32207: resolved
  // route-aware (policy store on the policy route, where the offer store is empty) so Home (Z) /
  // Rental (Y) coverage gating applies when editing an FO-6 risk on an existing policy.
  const { farmPolicyType } = useFarmPolicyContext();
  // Home (Z) and Rental (Y) are non-farm products and don't offer farm-only coverages (#32133).
  const isHomeOrRentalProduct = farmPolicyType === policyType.FarmHome || farmPolicyType === policyType.FarmRental;
  const hasBeenRated = !!item.id;
  const hasRceOrAcv = !!(
    item.building?.replacementCostEstimate?.total || item.building?.actualCashValueCalculation?.total
  );
  const state = address?.state;
  const ctOptions = getFO6ConstructionTypeOptions(state);
  const availableCoverageTypes =
    appConfig.farm[state]?.risks[riskType]?.allowedOptions?.scheduledCoverageTypes ||
    FarmScheduleCoverageTypesByState[state] ||
    [];
  // Get Mine Subsidence coverage limit from appConfig (GitHub issue #28634)
  const mineSubsidenceCoverageLimit = appConfig.farm?.[state]?.coverageLimits?.mineSubsidenceCoverageLimit || 750000;

  // Get building type from the item
  const buildingType = item.building?.buildingType;
  const availableStructureCategories = state ? structureCategoriesByState[state] || [] : [];
  const buildingTypeLabel = availableStructureCategories.find((option) => option.id === buildingType)?.value;

  // Use normal dwelling heating options for underwriting (FO-1/FO-2/FO-3 style)
  // These will be mapped to agricultural codes when sending to 360Value/Verisk
  const currentHeatingOptions = getDwellingHeatingOptionsForFO6();

  const isSelectedHeatingSourceUnitCountBased =
    currentHeatingOptions.find((option) => option.id === item.building?.primaryHeatingSource)?.unitCountBased ?? false;

  // Check field requirements based on occupancy type
  const isTowerOccupancy = item.building?.occupancy === 'GHT';
  const isBulkBinHopper = item.building?.occupancy === 'GHH';
  const isGrainBin = item.building?.occupancy === 'GHB';
  const shouldEnableDiameterHeight = isTowerOccupancy || isBulkBinHopper || isGrainBin;
  const shouldEnableSqFt = item.building?.occupancy && !isTowerOccupancy;

  // New BO occupancy types with specific business logic
  const isHayBarn = item.building?.occupancy === 'HB';

  const riskCoverages = getIn(values, fieldName)?.coverages || {};
  const initialCoverageE = getIn(initialValues, `${fieldName}.coverages.coverageE`);

  // Use the custom hook for business logic
  const {
    // Structure type flags
    isBarnOutbuilding,
    isPoultryBuilding,
    isDwellingContents,
    isFencing,
    isOpenSidedBuilding,
    isQuonsetBuilding,
    isHoopBuilding,

    // Barn business logic
    buildingClass,
    isRequiredFieldForClass,
    type1EligibilityValidation,

    // Validation and options
    coverageValidation,

    // Refs
    coverageHasBeenUpdatedRef
  } = useFO6BusinessLogic({
    item,
    fieldName,
    setFieldValue,
    buildingType,
    initialCoverageE,
    riskType,
    state,
    values,
    isUnderwriter: isUnderwriter(session)
  });

  // Additional structure type flags not in the hook
  const isStorageGrain = buildingType === StructureCategory.SilosGrainBins;
  const isConfinementBuilding = buildingType === StructureCategory.ConfinementBuildings;
  const isSolarPanels = buildingType === StructureCategory.SolarPanels;

  // Building age calculation for age-based RC eligibility (confinement and poultry buildings)
  // GitHub issue #28945
  const buildingAge = useMemo(() => getConfinementBuildingAge(item.building?.yearBuilt), [item.building?.yearBuilt]);
  const buildingMaxAgeThreshold = isConfinementBuilding
    ? (appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.confinementBuildingMaxAgeYears ?? 15)
    : isPoultryBuilding
      ? (appConfig?.farm?.[state]?.risks[riskType]?.businessRules?.poultryBuildingMaxAgeYears ?? 15)
      : 15;

  // Roof year validation for Poultry buildings
  const [poultryRoofYearError, setPoultryRoofYearError] = useState('');

  // Roof loss settlement validation
  const roofLossSettlementValidation = validateRoofLossSettlement(item.coverages);
  const [currentPoultryRoofYear, setCurrentPoultryRoofYear] = useState(item.building?.roofYear);
  const [currentPoultryYearBuilt, setCurrentPoultryYearBuilt] = useState(item.building?.yearBuilt);

  // Memoize roof loss settlement options to fix FastField rendering issue
  const roofLossSettlementOptions = useMemo(() => {
    return getRoofLossSettlementOptions({
      structureLossSettlement: item.coverages?.structureLossSettlement,
      buildingClass,
      isBarnOutbuilding,
      isConfinementBuilding,
      isPoultryBuilding,
      roofExclusionRestriction: item.coverages?.roofExclusionRestriction
    });
  }, [
    item.coverages?.structureLossSettlement,
    buildingClass,
    isBarnOutbuilding,
    isConfinementBuilding,
    isPoultryBuilding,
    item.coverages?.roofExclusionRestriction
  ]);

  // Sync local state with Formik values for Poultry buildings
  useEffect(() => {
    if (isPoultryBuilding) {
      const formikRoofYear = values[fieldName]?.building?.roofYear;
      const formikYearBuilt = values[fieldName]?.building?.yearBuilt;

      if (formikRoofYear !== currentPoultryRoofYear) {
        setCurrentPoultryRoofYear(formikRoofYear);
      }
      if (formikYearBuilt !== currentPoultryYearBuilt) {
        setCurrentPoultryYearBuilt(formikYearBuilt);
      }
    }
  }, [values, fieldName, isPoultryBuilding, currentPoultryRoofYear, currentPoultryYearBuilt]);

  // Validate Poultry roof year when roofYear or yearBuilt changes
  useEffect(() => {
    if (isPoultryBuilding) {
      const validation = validateRoofYear(currentPoultryRoofYear, currentPoultryYearBuilt);
      setPoultryRoofYearError(validation.isValid ? '' : validation.message);
    }
  }, [currentPoultryRoofYear, currentPoultryYearBuilt, isPoultryBuilding]);

  // Roof year validation for Dwelling buildings
  const [dwellingRoofYearError, setDwellingRoofYearError] = useState('');
  const [currentDwellingRoofYear, setCurrentDwellingRoofYear] = useState(item.building?.roofYear);
  const [currentDwellingYearBuilt, setCurrentDwellingYearBuilt] = useState(item.building?.yearBuilt);

  // Sync local state with Formik values for Dwelling buildings
  useEffect(() => {
    if (isDwellingContents) {
      const formikRoofYear = values[fieldName]?.building?.roofYear;
      const formikYearBuilt = values[fieldName]?.building?.yearBuilt;

      if (formikRoofYear !== currentDwellingRoofYear) {
        setCurrentDwellingRoofYear(formikRoofYear);
      }
      if (formikYearBuilt !== currentDwellingYearBuilt) {
        setCurrentDwellingYearBuilt(formikYearBuilt);
      }
    }
  }, [values, fieldName, isDwellingContents, currentDwellingRoofYear, currentDwellingYearBuilt]);

  // Validate Dwelling roof year when roofYear or yearBuilt changes
  useEffect(() => {
    if (isDwellingContents) {
      const validation = validateRoofYear(currentDwellingRoofYear, currentDwellingYearBuilt);
      setDwellingRoofYearError(validation.isValid ? '' : validation.message);
    }
  }, [currentDwellingRoofYear, currentDwellingYearBuilt, isDwellingContents]);

  // Auto-enable roof exclusion/restriction toggle for existing data
  const hasAutoEnabledRoofRestrictionRef = useRef(false);
  useEffect(() => {
    const currentRestriction = item.coverages?.roofExclusionRestriction;
    const currentToggle = item.coverages?.hasRoofExclusionRestriction;

    // Auto-enable toggle if restriction data exists but toggle is undefined (only once)
    if (
      currentRestriction &&
      currentRestriction !== 'none' &&
      currentToggle === undefined &&
      !hasAutoEnabledRoofRestrictionRef.current
    ) {
      setFieldValue(`${fieldName}.coverages.hasRoofExclusionRestriction`, true);
      hasAutoEnabledRoofRestrictionRef.current = true;
    }
  }, [item, fieldName, setFieldValue]);

  const initialBuilding = getIn(initialValues, `${fieldName}.building`);

  const toastRCEWarning = () => {
    if (coverageHasBeenUpdatedRef.current) {
      toast.notify({
        type: 'success',
        message:
          'Changing this item may affect the dwelling limit. After updating the offer, review the dwelling limit to ensure it is correct.'
      });
    }
  };

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">FO6 Structure: {buildingTypeLabel}</Label>
            <div className={classes.divider} />
            <Label type="infoSubtitleSmall">{item.description}</Label>
            <IconButton onClick={() => setRiskFieldnameToEdit(fieldName)} className={classes.editButton}>
              <img className={classes.editIcon} src={editIcon} alt="Edit row" />
            </IconButton>
          </div>
        </div>
      }
      isRemovable={canRemoveRisk}
      onDelete={() => removeRisk(index)}
      disableRemoveButton={disableRiskForm}
      open={isCurrentSectionOpen}
      onChange={toggleSection}
      id={fieldName}
    >
      {!hasBeenRated && (
        <Banner
          variant="warning"
          title="Data for this dwelling or structure isn't available—please enter the details manually."
          subtitle={
            inPolicyRoute
              ? "This information won't auto-fill, even if you preview changes."
              : "This information won't auto-fill, even if you update the offer."
          }
        />
      )}
      {buildingClass === 1 && !type1EligibilityValidation.isValid && (
        <Banner
          variant="error"
          title="Building Class 1 Requirements Not Met"
          subtitle={`${type1EligibilityValidation.message} Please select Building Class 2 or 3, or fix the building characteristics.`}
        />
      )}
      <Grid container className={classes.riskCard} spacing={2}>
        <Grid item xs={12}>
          <Label type="infoTitleSmall">Property</Label>
        </Grid>

        {isBarnOutbuilding && (
          <BarnOutbuildingFields
            fieldName={fieldName}
            buildingType={buildingType}
            farmPolicyType={farmPolicyType}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            currentHeatingOptions={currentHeatingOptions}
            isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
            isOpenSidedBuilding={isOpenSidedBuilding}
            isQuonsetBuilding={isQuonsetBuilding}
            isHayBarn={isHayBarn}
            item={item}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
          />
        )}

        {isStorageGrain && (
          <SilosGrainBinsFields
            fieldName={fieldName}
            buildingType={buildingType}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            shouldEnableDiameterHeight={shouldEnableDiameterHeight}
            shouldEnableSqFt={shouldEnableSqFt}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
            occupancy={item.building?.occupancy}
          />
        )}

        {isPoultryBuilding && (
          <>
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
                setCurrentPoultryYearBuilt(val);
                setFieldValue(`${fieldName}.building.yearBuilt`, val);
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.roofYear`}
              name={`${fieldName}.building.roofYear`}
              mode="light"
              label="Roof year"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              error={!!poultryRoofYearError || !!getIn(errors, `${fieldName}.building.roofYear`)}
              helperText={poultryRoofYearError || getIn(errors, `${fieldName}.building.roofYear`) || undefined}
              onChange={(val) => {
                if (val !== initialBuilding.roofYear) {
                  toastRCEWarning();
                }
                setCurrentPoultryRoofYear(val);
                setFieldValue(`${fieldName}.building.roofYear`, val);
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="select"
              id={`${fieldName}.building.roofType`}
              name={`${fieldName}.building.roofType`}
              mode="light"
              label="Roof type"
              options={barnRtOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.roofType) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4}>
              <LabelTooltip
                label="Solid Fuel Burning Appliances"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                }}
                mode="light"
              >
                <Field
                  type="numeric"
                  id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  mode="light"
                  disabled={disableRiskForm}
                  format={{
                    allowNegative: false
                  }}
                  onChange={(val) => {
                    if (val !== initialBuilding.numSolidFuelBurningAppliances) {
                      toastRCEWarning();
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.primaryHeatingSource`}
              name={`${fieldName}.building.primaryHeatingSource`}
              mode="light"
              label="Primary heating"
              options={currentHeatingOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.primaryHeatingSource) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {isFencing && (
          <Field
            xs={6}
            type="numeric"
            id={`${fieldName}.building.yearBuilt`}
            name={`${fieldName}.building.yearBuilt`}
            mode="light"
            label="Year built in"
            format={{
              allowNegative: false,
              isAllowed: ({ value }) => value.length <= 4
            }}
            disabled={disableRiskForm}
          />
        )}

        {isSolarPanels && (
          <>
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="numeric"
              id={`${fieldName}.building.numSolarPanels`}
              name={`${fieldName}.building.numSolarPanels`}
              mode="light"
              label="Number of panels"
              format={{
                allowNegative: false
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.numSolarPanels) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4} />
            <Grid item xs={12}>
              <Label type="infoTitleSmall">Mounted on</Label>
            </Grid>
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.solarMountDwelling`}
              name={`${fieldName}.building.solarMountDwelling`}
              mode="light"
              label="Dwelling"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.solarMountDwelling) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.solarMountOutbuilding`}
              name={`${fieldName}.building.solarMountOutbuilding`}
              mode="light"
              label="Outbuilding"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.solarMountOutbuilding) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="checkbox"
              id={`${fieldName}.building.solarMountGround`}
              name={`${fieldName}.building.solarMountGround`}
              mode="light"
              label="Ground"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.solarMountGround) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {isConfinementBuilding && (
          <ConfinementBuildingFields
            fieldName={fieldName}
            buildingType={buildingType}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            currentHeatingOptions={currentHeatingOptions}
            isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
          />
        )}

        {isHoopBuilding && (
          <HoopBuildingFields
            fieldName={fieldName}
            initialBuilding={initialBuilding}
            disableRiskForm={disableRiskForm}
            currentHeatingOptions={currentHeatingOptions}
            isSelectedHeatingSourceUnitCountBased={isSelectedHeatingSourceUnitCountBased}
            toastRCEWarning={toastRCEWarning}
            ctOptions={ctOptions}
          />
        )}

        {isDwellingContents && (
          <>
            <Field
              xs={6}
              type="select"
              id={`${fieldName}.building.residenceType`}
              name={`${fieldName}.building.residenceType`}
              mode="light"
              label="Residence type/usage"
              options={[
                { id: 'rental', value: 'Rental' },
                { id: 'seasonal', value: 'Seasonal' },
                { id: 'vacant', value: 'Vacant' }
              ]}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.residenceType) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.yearBuilt`}
              name={`${fieldName}.building.yearBuilt`}
              mode="light"
              label="Year built in"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.yearBuilt) {
                  toastRCEWarning();
                }
                setCurrentDwellingYearBuilt(val);
                setFieldValue(`${fieldName}.building.yearBuilt`, val);
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.roofYear`}
              name={`${fieldName}.building.roofYear`}
              mode="light"
              label="Roof year"
              format={{
                allowNegative: false,
                isAllowed: ({ value }) => value.length <= 4
              }}
              disabled={disableRiskForm}
              error={!!getIn(errors, `${fieldName}.building.roofYear`) || !!dwellingRoofYearError}
              helperText={getIn(errors, `${fieldName}.building.roofYear`) || dwellingRoofYearError || undefined}
              onChange={(val) => {
                if (val !== initialBuilding.roofYear) {
                  toastRCEWarning();
                }
                setCurrentDwellingRoofYear(val);
                setFieldValue(`${fieldName}.building.roofYear`, val);
              }}
            />
            <Field
              xs={3}
              type="numeric"
              id={`${fieldName}.building.sqFt`}
              name={`${fieldName}.building.sqFt`}
              mode="light"
              label="Total sqft"
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.sqFt) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={3}
              type="select"
              id={`${fieldName}.building.buildingQuality`}
              name={`${fieldName}.building.buildingQuality`}
              mode="light"
              label="Quality"
              options={bqOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.buildingQuality) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.roofType`}
              name={`${fieldName}.building.roofType`}
              mode="light"
              label="Roof type"
              options={barnRtOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.roofType) {
                  toastRCEWarning();
                }
              }}
            />
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.constructionType`}
              name={`${fieldName}.building.constructionType`}
              mode="light"
              label="Construction type"
              options={ctOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.constructionType) {
                  toastRCEWarning();
                }
              }}
            />
            <Grid item xs={4}>
              <LabelTooltip
                label="Solid Fuel Burning Appliances"
                tooltip={{
                  label: 'More Info',
                  onHoverText:
                    'Solid Fuel or wood heat examples include standard fireplace, fireplace with insert, indoor solid fuel or wood heat (including pellet stoves), outside solid fuel or wood heat. Barrel or Homemade stoves are Not Acceptable.'
                }}
                mode="light"
              >
                <Field
                  type="numeric"
                  id={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  name={`${fieldName}.building.numSolidFuelBurningAppliances`}
                  mode="light"
                  disabled={disableRiskForm}
                  format={{
                    allowNegative: false
                  }}
                  onChange={(val) => {
                    if (val !== initialBuilding.numSolidFuelBurningAppliances) {
                      toastRCEWarning();
                    }
                  }}
                />
              </LabelTooltip>
            </Grid>
            <Field
              xs={4}
              type="select"
              id={`${fieldName}.building.primaryHeatingSource`}
              name={`${fieldName}.building.primaryHeatingSource`}
              mode="light"
              label="Primary heating"
              options={currentHeatingOptions}
              disabled={disableRiskForm}
              onChange={(val) => {
                if (val !== initialBuilding.primaryHeatingSource) {
                  toastRCEWarning();
                }
              }}
            />
          </>
        )}

        {(hasBeenRated || coverageHasBeenUpdatedRef.current) && !isFencing && !isDwellingContents && (
          <NotificationCard className={classes.notification} iconSize="icon16" type="infoGreen">
            <Grid container className={classes.rceLabel}>
              <Label>Our estimated cost to replace this structure</Label>
              <Tooltip
                label="More info"
                text="RCE (Replacement Cost Estimate) and ACV (Actual Cash Value) are calculated using 360Value. RCE is the cost to replace the structure with new materials. ACV accounts for depreciation based on age and condition. The selected loss settlement option determines which value applies to the claim."
              />
            </Grid>
            <Grid container item xs={4} spacing={2} justifyContent="flex-end">
              <Grid item className={classes.rceValues}>
                <Label>
                  RCE
                  {item.coverages?.structureLossSettlement === 'RC' ||
                  item.coverages?.structureLossSettlement === 'FUNC' ||
                  item.coverages?.roofLossSettlement === 'RC_ACV_WIND_HAIL'
                    ? ' (Active)'
                    : ''}
                </Label>
                <Label type="infoValueBold">
                  {item.building?.replacementCostEstimate?.total
                    ? `$${item.building.replacementCostEstimate.total.toLocaleString('en-US')}`
                    : 'N/A'}
                </Label>
              </Grid>
              {item.building?.actualCashValueCalculation?.total && (
                <Grid item className={classes.rceValues}>
                  <Label>
                    ACV
                    {item.coverages?.structureLossSettlement === 'ACV' ? ' (Active)' : ''}
                  </Label>
                  <Label type="infoValueBold">
                    ${item.building.actualCashValueCalculation.total.toLocaleString('en-US')}
                  </Label>
                </Grid>
              )}
            </Grid>
          </NotificationCard>
        )}
      </Grid>

      {(isDwellingContents || isFencing || hasBeenRated || hasRceOrAcv) && (
        <Grid container spacing={2} className={classes.coverageCard}>
          {isDwellingContents && (
            <>
              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle">Base coverages</Label>
              </Grid>
              <Grid item xs={6}>
                <LabelTooltip
                  label="Coverage E - Dwelling Limit"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Coverage limit for the dwelling structure itself, protecting against damage from covered perils.'
                  }}
                >
                  <Field
                    id={`${fieldName}.coverages.coverageE`}
                    name={`${fieldName}.coverages.coverageE`}
                    type="numeric"
                    mode="dark"
                    // #31830/#31831: FastField (the default) drops the dwelling limit edit on the
                    // first DC structure when a data-pull/reinitialize re-renders the form mid-edit,
                    // so it reverts to the $20,000 default. Force a regular Field so the entered
                    // value always commits — matching the rated Coverage E field below.
                    fast={false}
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disableRiskForm}
                  />
                </LabelTooltip>
              </Grid>
              <Grid item xs={6}>
                <LabelTooltip
                  label="Coverage E - Contents Limit"
                  tooltip={{
                    label: 'More Info',
                    onHoverText:
                      'Coverage limit for contents within the dwelling, protecting personal property from covered perils.'
                  }}
                >
                  <Field
                    id={`${fieldName}.coverages.contentsLimit`}
                    name={`${fieldName}.coverages.contentsLimit`}
                    type="numeric"
                    mode="dark"
                    // #31830/#31831: see Coverage E above — keep the contents limit on a regular
                    // Field so it doesn't revert to its default the same way.
                    fast={false}
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disableRiskForm}
                    defaultValue={0}
                    error={(riskCoverages.contentsLimit ?? 0) > (riskCoverages.coverageE ?? 0)}
                    helperText={
                      (riskCoverages.contentsLimit ?? 0) > (riskCoverages.coverageE ?? 0)
                        ? 'Contents Limit cannot exceed Dwelling Limit'
                        : undefined
                    }
                  />
                </LabelTooltip>
              </Grid>
            </>
          )}
          {!isDwellingContents && (isFencing || hasBeenRated || hasRceOrAcv) && (
            <>
              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle">Base coverages</Label>
              </Grid>
              <Grid item xs={12}>
                <LabelTooltip
                  label={isFencing ? 'Fencing Coverage Limit' : 'Scheduled Property Limit (E)'}
                  tooltip={{
                    label: 'More Info',
                    onHoverText: isFencing
                      ? 'Coverage limit for fencing structures, protecting against damage from covered perils.'
                      : 'Coverage for buildings and structures on the property, protecting against damage from covered perils.'
                  }}
                >
                  <Field
                    id={`${fieldName}.coverages.coverageE`}
                    name={`${fieldName}.coverages.coverageE`}
                    type="numeric"
                    mode="dark"
                    fast={false}
                    format={{ thousandSeparator: true, prefix: '$' }}
                    disabled={disableRiskForm}
                    description={
                      isFencing
                        ? undefined
                        : item.coverages?.canEditCoverageAmount && !getIn(errors, `${fieldName}.coverages.coverageE`)
                          ? item.coverages?.structureLossSettlement === 'ACV' &&
                            item.building?.actualCashValueCalculation?.total
                            ? `You may adjust this from $${Math.round(item.building.actualCashValueCalculation.total * 0.8).toLocaleString()} (80% ACV)${item.building?.replacementCostEstimate?.total ? ` to $${item.building.replacementCostEstimate.total.toLocaleString()} (100% RCE)` : ''}.`
                            : `You may adjust this from $${(initialCoverageE * 0.8).toLocaleString()} (80% RCE) to $${initialCoverageE.toLocaleString()} (100% RCE).`
                          : undefined
                    }
                    helperText={
                      !isFencing &&
                      coverageValidation.message &&
                      !(item.coverages?.canEditCoverageAmount && !getIn(errors, `${fieldName}.coverages.coverageE`))
                        ? coverageValidation.message
                        : undefined
                    }
                    error={!isFencing && !coverageValidation.isValid}
                  />
                </LabelTooltip>
              </Grid>

              {!isFencing && (
                <div className={classes.coverageToggleContainer}>
                  <Grid container justifyContent="space-between">
                    <Field
                      id={`${fieldName}.coverages.canEditCoverageAmount`}
                      name={`${fieldName}.coverages.canEditCoverageAmount`}
                      type="switch"
                      mode="dark"
                      label="Adjust scheduled property limit"
                      tooltipText="By default (and when toggled off), the scheduled property limit matches our estimated replacement cost. If you have an external estimate, you can adjust the limit here. Supporting documentation will be required."
                      tooltipLabel="More Info"
                      xs={8}
                      disabled={coverageHasBeenUpdatedRef.current}
                    />
                    <Grid className={classes.linkAndIcon}>
                      <a
                        rel="noreferrer noopener"
                        target="_blank"
                        href="https://www.360-value.com/apps/iv/ui/#/login"
                        className={classes.link}
                      >
                        ISO 360 Login
                        <img src={externalLinkIcon} alt="link to ISO 360 login" />
                      </a>
                    </Grid>

                    {item.coverages?.canEditCoverageAmount && (
                      <Grid item xs={12} className={classes.adjustCoverageSubContainer}>
                        <Field
                          id={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                          name={`${fieldName}.coverages.adjustDwellingLimitDescription`}
                          type="textarea"
                          mode="dark"
                          label="Description of reason for adjusting scheduled property limit (required)"
                          xs={12}
                          rows={3}
                        />
                      </Grid>
                    )}
                  </Grid>
                </div>
              )}
            </>
          )}

          {(isFencing || hasBeenRated || hasRceOrAcv) && (
            <>
              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle" className={classes.sectionDivider}>
                  Deductibles
                </Label>
              </Grid>
              <BrandFormField
                xs={4}
                id={`${fieldName}.coverages.deductibleAllOther`}
                name={`${fieldName}.coverages.deductibleAllOther`}
                type="select"
                mode="dark"
                label="All Peril Deductible"
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
              />
              <BrandFormField
                xs={4}
                id={`${fieldName}.coverages.deductibleWindHail`}
                name={`${fieldName}.coverages.deductibleWindHail`}
                type="select"
                options={windHailDeductibleOptions}
                mode="dark"
                label="Wind & Hail Deductible"
                disabled={disableRiskForm}
                riskType={riskType}
                coverages={item.coverages}
                state={state}
                coverage={item.coverages?.coverageE}
                description="Wind and hail deductible must match an All Peril when it's value is greater than 2% of coverage E"
              />

              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle" className={classes.sectionDivider}>
                  Optional Coverages
                </Label>
              </Grid>
              <Field
                xs={6}
                id={`${fieldName}.coverages.structureLossSettlement`}
                name={`${fieldName}.coverages.structureLossSettlement`}
                type="select"
                options={getLossSettlementOptions({
                  isDwellingContents,
                  isHoopBuilding,
                  isFencing,
                  isStorageGrain,
                  isConfinementBuilding,
                  isPoultryBuilding,
                  isBarnOutbuilding,
                  buildingClass,
                  session,
                  buildingAge,
                  maxAgeThreshold: buildingMaxAgeThreshold
                })}
                mode="dark"
                label="Structure Loss Settlement"
                disabled={disableRiskForm || isRequiredFieldForClass('structureLossSettlement')}
                helperText={
                  isBarnOutbuilding && buildingClass && (buildingClass === 1 || buildingClass === 2)
                    ? `Required as Replacement Cost for Building Class ${buildingClass}`
                    : undefined
                }
              />
              {!isFencing && (
                <BrandFormField
                  xs={6}
                  id={`${fieldName}.coverages.roofLossSettlement`}
                  name={`${fieldName}.coverages.roofLossSettlement`}
                  type="select"
                  options={roofLossSettlementOptions}
                  // need this key so that the field re-renders when the options change
                  key={`${fieldName}.roofLossSettlement.${buildingClass}.${roofLossSettlementOptions.length}`}
                  mode="dark"
                  label="Roof Loss Settlement"
                  disabled={disableRiskForm}
                  error={!roofLossSettlementValidation.isValid}
                  helperText={roofLossSettlementValidation.message || undefined}
                  riskType={riskType}
                  state={state}
                />
              )}

              <BrandFormField
                name={`${fieldName}.coverages.coverageMS`}
                id={`${fieldName}.coverages.coverageMS`}
                type="select"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
                riskType={riskType}
                state={state}
                coverages={item.coverages}
                county={address?.county}
                targetCoverage={item.coverages?.coverageE}
                targetCoverageLimit={mineSubsidenceCoverageLimit}
                helperTextLabel="Matches coverage E"
              />
              <Field
                name={`${fieldName}.coverages.hasActiveCoverageMS`}
                id={`${fieldName}.coverages.hasActiveCoverageMS`}
                type="checkbox"
                label="This structure has active mine subsidence or unrepaired mine damage"
                mode="dark"
                xs={6}
                disabled={disableRiskForm}
              />

              <Field
                xs={6}
                name={`${fieldName}.coverages.specialFormCoverage`}
                id={`${fieldName}.coverages.specialFormCoverage`}
                type="checkbox"
                label={`Special form coverage${isBarnOutbuilding && buildingClass ? ` (${buildingClass === 1 || buildingClass === 2 ? 'Required' : 'Excluded'} for Building Class ${buildingClass})` : ''}`}
                mode="dark"
                disabled={
                  disableRiskForm ||
                  (isBarnOutbuilding && (buildingClass === 3 || isRequiredFieldForClass('specialFormCoverage'))) ||
                  ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') ||
                  (!isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV')
                }
                helperText={
                  !isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV'
                    ? 'Not available with Actual Cash Value loss settlement'
                    : (isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC'
                      ? 'Required for Replacement Cost building loss settlement'
                      : isBarnOutbuilding && buildingClass
                        ? buildingClass === 1 || buildingClass === 2
                          ? `Building Class ${buildingClass}: RC + Special Form + Snow/Ice required`
                          : buildingClass === 3
                            ? `Building Class ${buildingClass}: ACV only, Special Form and Snow/Ice excluded`
                            : ''
                        : ''
                }
              />
              <Field
                xs={6}
                name={`${fieldName}.coverages.hasReplacementCostCoverage`}
                id={`${fieldName}.coverages.hasReplacementCostCoverage`}
                type="checkbox"
                label="Replacement cost provision"
                mode="dark"
                disabled={
                  disableRiskForm ||
                  (isBarnOutbuilding &&
                    (buildingClass === 3 || isRequiredFieldForClass('hasReplacementCostCoverage'))) ||
                  ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') ||
                  (!isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV')
                }
                helperText={
                  !isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV'
                    ? 'Not available with Actual Cash Value loss settlement'
                    : (isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC'
                      ? 'Required for Replacement Cost building loss settlement'
                      : isBarnOutbuilding && buildingClass
                        ? buildingClass === 1 || buildingClass === 2
                          ? `Building Class ${buildingClass}: RC + Special Form + Snow/Ice + Replacement Cost Provision required`
                          : buildingClass === 3
                            ? 'Building Class 3: ACV only, Special Form, Snow/Ice, and Replacement Cost Provision excluded'
                            : undefined
                        : undefined
                }
              />

              <BrandFormField
                xs={6}
                name={`${fieldName}.coverages.repairRebuildRequirement`}
                id={`${fieldName}.coverages.repairRebuildRequirement`}
                type="checkbox"
                label="Repair or rebuilding requirement"
                mode="dark"
                disabled={disableRiskForm}
                riskType={riskType}
                riskFieldName={fieldName}
                state={state}
              />
              <Field
                xs={6}
                name={`${fieldName}.coverages.buildingMaterialTheft`}
                id={`${fieldName}.coverages.buildingMaterialTheft`}
                type="checkbox"
                label="Theft of building materials"
                mode="dark"
                disabled={disableRiskForm}
              />

              <Field
                xs={6}
                name={`${fieldName}.coverages.hasSnowWeightCoverage`}
                id={`${fieldName}.coverages.hasSnowWeightCoverage`}
                type="checkbox"
                label={`Weight of ice, snow, or sleet coverage${isBarnOutbuilding && buildingClass ? ` (${buildingClass === 1 || buildingClass === 2 ? 'Required' : 'Excluded'} for Building Class ${buildingClass})` : ''}`}
                mode="dark"
                disabled={
                  disableRiskForm ||
                  (isBarnOutbuilding && (buildingClass === 3 || isRequiredFieldForClass('hasSnowWeightCoverage'))) ||
                  ((isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC') ||
                  (!isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV')
                }
                helperText={
                  !isBarnOutbuilding && item.coverages?.structureLossSettlement === 'ACV'
                    ? 'Not available with Actual Cash Value loss settlement'
                    : (isConfinementBuilding || isPoultryBuilding) && item.coverages?.structureLossSettlement === 'RC'
                      ? 'Required for Replacement Cost building loss settlement'
                      : isBarnOutbuilding && buildingClass
                        ? buildingClass === 1 || buildingClass === 2
                          ? `Building Class ${buildingClass}: RC + Special Form + Snow/Ice required`
                          : buildingClass === 3
                            ? `Building Class ${buildingClass}: ACV only, Special Form and Snow/Ice excluded`
                            : undefined
                        : undefined
                }
              />

              <Grid item xs={12} className={classes.bottomMargin}>
                <Label type="formSubTitle" className={classes.sectionDivider}>
                  Exclusions
                </Label>
              </Grid>
              <Field
                xs={6}
                type="switch"
                id={`${fieldName}.coverages.cosmeticDamageExclusion`}
                name={`${fieldName}.coverages.cosmeticDamageExclusion`}
                mode="dark"
                label="Cosmetic damage exclusion"
                disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
              />

              {item.coverages?.cosmeticDamageExclusion && (
                <>
                  <Grid item xs={12}>
                    <Label type="infoTitleSmall">APPLY COSMETIC DAMAGE EXCLUSION TO THESE SURFACING TYPES:</Label>
                  </Grid>
                  <Field
                    xs={4}
                    type="checkbox"
                    id={`${fieldName}.coverages.exteriorDoorsWindowsDamageExclusion`}
                    name={`${fieldName}.coverages.exteriorDoorsWindowsDamageExclusion`}
                    mode="dark"
                    label="Exterior doors & windows"
                    disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
                  />
                  <Field
                    xs={4}
                    type="checkbox"
                    id={`${fieldName}.coverages.exteriorWallsDamageExclusion`}
                    name={`${fieldName}.coverages.exteriorWallsDamageExclusion`}
                    mode="dark"
                    label="Exterior walls"
                    disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
                  />
                  <Field
                    xs={4}
                    type="checkbox"
                    id={`${fieldName}.coverages.roofsDamageExclusion`}
                    name={`${fieldName}.coverages.roofsDamageExclusion`}
                    mode="dark"
                    label="Roofs"
                    disabled={disableRiskForm || riskType === FarmRiskType.Fo6Structures}
                  />
                </>
              )}
              <Field
                xs={6}
                type="switch"
                id={`${fieldName}.coverages.hasRoofExclusionRestriction`}
                name={`${fieldName}.coverages.hasRoofExclusionRestriction`}
                mode="dark"
                label="Roof exclusion/restriction"
                disabled={disableRiskForm}
              />

              {item.coverages?.hasRoofExclusionRestriction && (
                <>
                  <Grid item xs={12}>
                    <Label type="infoTitleSmall">ROOF EXCLUSION/RESTRICTION DETAILS:</Label>
                  </Grid>
                  <Field
                    xs={6}
                    id={`${fieldName}.coverages.roofExclusionRestriction`}
                    name={`${fieldName}.coverages.roofExclusionRestriction`}
                    type="select"
                    options={[
                      { id: 'excluded', value: 'Roof Excluded' },
                      { id: 'ACV', value: 'Roof Actual Cash Value' }
                    ]}
                    mode="dark"
                    label="Roof exclusion/Restriction"
                    disabled={disableRiskForm}
                  />
                  <Field
                    xs={12}
                    id={`${fieldName}.coverages.roofExclusionDescription`}
                    name={`${fieldName}.coverages.roofExclusionDescription`}
                    type="textarea"
                    mode="dark"
                    label="Description of reason for excluding the selected items (required)"
                    rows={3}
                    disabled={disableRiskForm}
                  />
                </>
              )}

              <ExteriorWallSurfacingExclusionSection fieldName={`${fieldName}.coverages`} disabled={disableRiskForm} />

              {/* unscheduled personal property */}
              <UnscheduledPersonalPropertyForm
                fieldName={`${fieldName}.coverages`}
                disabled={disableRiskForm}
                state={state}
                riskType={riskType}
              />

              {/* scheduled personal property (inland marine coverage) */}
              {availableCoverageTypes.includes(FarmScheduleCoverageType.SPP) && (
                <InlandMarineForm
                  fieldName={`${fieldName}.coverages.scheduleCoverages`}
                  disabled={disableRiskForm}
                  state={state}
                  riskType={FarmRiskType.Fo6Structures}
                  coverages={riskCoverages}
                />
              )}

              {/* Related Private Structures */}
              {availableCoverageTypes.includes(FarmScheduleCoverageType.RELATED_PRIVATE_STRUCTURES) && (
                <RelatedPrivateStructuresForm
                  fieldName={`${fieldName}.coverages.scheduleCoverages`}
                  disabled={disableRiskForm}
                  address={locationAddress}
                  riskType={FarmRiskType.Fo6Structures}
                  coverages={riskCoverages}
                />
              )}

              {/* Farm Income Coverage - Hide for Solar Panels, Fencing, Dwelling/Contents, and the
                  non-farm Home (Z) / Rental (Y) products (Farm Income is farm-only, #32133) */}
              {!isSolarPanels &&
                !isFencing &&
                !isDwellingContents &&
                !isHomeOrRentalProduct &&
                availableCoverageTypes.includes(FarmScheduleCoverageType.INCOME) && (
                  <FarmIncomeCoverageForm
                    fieldName={`${fieldName}.coverages.scheduleCoverages`}
                    disabled={disableRiskForm}
                    variant="E"
                  />
                )}
            </>
          )}
        </Grid>
      )}
    </CollapsibleCard>
  );
};

FO6.propTypes = {
  item: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  fieldName: PropTypes.string.isRequired,
  canRemoveRisk: PropTypes.bool.isRequired,
  removeRisk: PropTypes.func.isRequired,
  setRiskFieldnameToEdit: PropTypes.func.isRequired,
  locationAddress: PropTypes.object.isRequired
};

export default observer(withDatePicker(FO6));
